import { ArmazenamentoService } from '@/core/services/armazenamento/armazenamento.service';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';

/** `sistema` segue o `prefers-color-scheme` do sistema operacional. */
export type Tema = 'light' | 'dark' | 'sistema';

export const TEMA_PADRAO: Tema = 'dark';

const TEMAS: readonly Tema[] = ['light', 'dark', 'sistema'];

// Classes que o SCSS legado lê no <body> (ex.: notificacao).
const CLASSES_BODY = ['light', 'dark'];

const CSS_LARA: Record<'light' | 'dark', string> = {
  light: 'assets/themes/lara-light-blue/theme.css',
  dark: 'assets/themes/lara-dark-blue/theme.css',
};

/**
 * Tema da aplicação (claro, escuro ou o do sistema), persistido por usuário.
 *
 * Aplica no `<body>` a classe do tema e o `data-bs-theme` (lido pelo Bootstrap, pelos tokens de
 * `tema.scss` e pelo `dark:` do Tailwind), o `color-scheme` no `<html>` e o CSS estático `lara-*`.
 */
@Injectable({ providedIn: 'root' })
export class TemaService {
  private readonly document = inject(DOCUMENT);
  private readonly armazenamento = inject(ArmazenamentoService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly atual = signal<Tema>(TEMA_PADRAO);
  private readonly sistemaEscuro = signal(false);
  private chaveArmazenamento: string | null = null;

  /** Tema escolhido (pode ser `sistema`). */
  readonly tema = this.atual.asReadonly();

  /** Tema efetivamente aplicado, com `sistema` já resolvido. */
  readonly temaAplicado = computed<Exclude<Tema, 'sistema'>>(() => {
    const tema = this.atual();
    if (tema !== 'sistema') return tema;
    return this.sistemaEscuro() ? 'dark' : 'light';
  });

  readonly escuro = computed(() => this.temaAplicado() !== 'light');

  constructor() {
    this.observarSistema();
    effect(() => this.aplicar(this.temaAplicado()));
  }

  /** Lê o tema salvo do usuário (ou aplica o padrão). Chamar quando o usuário logado for conhecido. */
  carregar(idUsuario: string | number): void {
    this.chaveArmazenamento = `thema_${idUsuario}`;
    const salvo = this.armazenamento.obterItem(this.chaveArmazenamento) as string | null;
    // O tema "Tuca" (dark_degrade) foi removido: quem o tinha salvo passa ao escuro.
    if (salvo === 'dark_degrade') {
      this.definir('dark');
      return;
    }
    this.atual.set(salvo && TEMAS.includes(salvo as Tema) ? (salvo as Tema) : TEMA_PADRAO);
  }

  definir(tema: Tema): void {
    this.atual.set(tema);
    if (this.chaveArmazenamento) this.armazenamento.definirItem(this.chaveArmazenamento, tema);
  }

  /** Alterna entre claro e escuro (sai de `sistema`). */
  alternar(): void {
    this.definir(this.escuro() ? 'light' : 'dark');
  }

  private aplicar(tema: Exclude<Tema, 'sistema'>): void {
    if (!this.isBrowser) return;
    const modo = tema === 'light' ? 'light' : 'dark';
    const body = this.document.body;

    body.classList.remove(...CLASSES_BODY);
    body.classList.add(tema);
    body.setAttribute('data-bs-theme', modo);
    this.document.documentElement.style.colorScheme = modo;

    const lara = this.document.getElementById('app-theme') as HTMLLinkElement | null;
    if (lara && !lara.href.endsWith(CSS_LARA[modo])) lara.href = CSS_LARA[modo];
  }

  private observarSistema(): void {
    if (!this.isBrowser) return;
    const consulta = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
    if (!consulta) return;

    this.sistemaEscuro.set(consulta.matches);
    const aoMudar = (evento: MediaQueryListEvent) => this.sistemaEscuro.set(evento.matches);
    consulta.addEventListener('change', aoMudar);
    inject(DestroyRef).onDestroy(() => consulta.removeEventListener('change', aoMudar));
  }
}
